import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, MessageCircle, Printer, Check, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/site/StatusBadge";
import { MessageThread } from "@/components/site/MessageThread";
import { CheckRow } from "@/components/site/FormBits";
import { Spinner } from "@/components/site/ProtectedRoute";
import { decideApplication } from "@/pages/HostDashboard";
import { StartCallButton } from "@/components/site/CallWindow";
import { SectionThread, useDayComments } from "@/components/course/SectionThread";
import { AnswerAdvice } from "@/components/course/AnswerAdvice";
import { AiInsights, useInsights } from "@/components/course/AiInsights";
import { HostCertificates } from "@/components/site/HostCertificates";
import { HostQuestionnaire } from "@/components/course/QuestionnaireResult";
import { HostAvenueAccess } from "@/components/avenues/AvenueParts";
import { Avatar } from "@/components/site/AvatarUpload";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { fetchCourse, dayFields } from "@/lib/course";
import { useSeo } from "@/lib/seo";
import { APP_STAGES, COURSE_FORMATS } from "@/lib/constants";

const KV = ({ k, v }) => (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length) ? null : (
  <div className="grid sm:grid-cols-[200px_1fr] gap-1 py-2.5 border-b border-[#0B2A4A]/5 text-sm">
    <dt className="font-semibold text-[#0B2A4A]">{k}</dt>
    <dd className="text-slate-700 whitespace-pre-wrap">{Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Yes" : "No") : v}</dd>
  </div>
));

function LeadCard({ lead, onUpdate }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const update = (data) => api.patch(`/admin/leads/${lead.id}`, data).then(() => { toast.success("Lead updated"); onUpdate(); }).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6" data-testid="person-lead-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-[#0B2A4A]">{lead.interest || "Digital Nomad Nicaragua"} inquiry · {fmtDate(lead.created_at)}</h3>
        <select value={lead.status} onChange={(e) => update({ status: e.target.value })} className="rounded-lg border border-[#0B2A4A]/20 px-3 py-1.5 text-sm bg-white" data-testid="lead-status-select">
          {["new", "contacted", "enrolled", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <dl>
        <KV k="Interested in" v={lead.interest} /><KV k="Journey / level" v={lead.journey_stage} /><KV k="Working toward" v={lead.goals} /><KV k="Skills" v={lead.skills} />
        <KV k="Can offer" v={lead.offer} /><KV k="Collaboration" v={lead.collaboration} /><KV k="Existing project" v={lead.existing_project} />
        <KV k="WhatsApp" v={lead.whatsapp} /><KV k="WhatsApp consent" v={lead.whatsapp_consent} /><KV k="Preferred contact" v={lead.contact_pref} /><KV k="Retreat updates" v={lead.retreat_optin} />
        <KV k="Add-ons" v={lead.add_ons} /><KV k="Other add-on" v={lead.add_ons_other} />
      </dl>
      <div className="mt-4 flex flex-col gap-2">
        <label className="text-sm font-semibold text-[#0B2A4A]" htmlFor={`notes-${lead.id}`}>Private host notes</label>
        <Textarea id={`notes-${lead.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes-input" />
        <button onClick={() => update({ notes })} className="self-start rounded-lg bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2 text-sm font-semibold" data-testid="lead-notes-save-btn">Save notes</button>
      </div>
    </div>
  );
}

function DayWork({ day, ws, rec, userId, insights }) {
  const [fb, setFb] = useState(rec?.feedback || "");
  const answers = rec?.answers || {};
  const save = () => api.post(`/admin/progress/${userId}/${day.day}/feedback`, { feedback: fb }).then(() => toast.success("Feedback saved & student notified")).catch((e) => toast.error(formatApiError(e)));
  const groups = dayFields(day, ws).reduce((acc, f) => ({ ...acc, [f.section]: { label: f.group, fields: [...(acc[f.section]?.fields || []), f] } }), {});
  const { comments, post, markRead } = useDayComments(day.day, userId);
  const newCount = comments.filter((c) => !c.read_by_host).length;
  return (
    <details className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 group" data-testid={`host-daywork-${day.day}`}>
      <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-2 list-none">
        <span className="font-semibold text-[#0B2A4A]"><span className="font-mono-ui text-[#F08A3C] mr-2">DAY {day.day}</span>{day.title}</span>
        <span className="flex items-center gap-2">{newCount > 0 && <span className="rounded-full bg-[#F08A3C] text-white text-[10px] px-2 py-0.5" data-testid={`host-daywork-new-${day.day}`}>{newCount} new</span>}<StatusBadge status={rec?.status} />{rec?.updated_at && <span className="text-xs text-slate-500 font-mono-ui">{fmtDate(rec.updated_at)}</span>}
          <Link to={`/host/teach/${userId}/day/${day.day}`} onClick={(e) => e.stopPropagation()} className="rounded-lg bg-[#1A9AA8] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#147C88]" data-testid={`host-start-lesson-${day.day}`}>Start lesson</Link></span>
      </summary>
      {!rec ? (
        <div className="mt-4"><p className="text-sm text-slate-500">No saved work yet.</p><SectionThread section="general" comments={comments} post={post} markRead={markRead} me="host" label="Conversation about this day" /></div>
      ) : (
        <div className="mt-5 flex flex-col gap-5">
          <AiInsights studentId={userId} scope={`day:${day.day}`} list={insights.list} reload={insights.load} title={`AI insights for Day ${day.day}`} />
          {Object.entries(groups).map(([sec, g]) => (
            <div key={sec}><p className="text-[11px] font-bold uppercase tracking-wider text-[#147C88] mb-1">{g.label}</p><dl>{g.fields.map((f) => <div key={f.id}><KV k={f.prompt} v={answers[f.id] || "—"} /><div className="pb-3"><AnswerAdvice fieldId={f.id} comments={comments} post={post} markRead={markRead} me="host" /></div></div>)}</dl>
              <SectionThread section={sec} comments={comments} post={post} markRead={markRead} me="host" /></div>
          ))}
          <div><p className="text-[11px] font-bold uppercase tracking-wider text-[#147C88] mb-1">Deliverables</p>
            <ul className="text-sm space-y-1">{day.deliverables.map((d) => <li key={d.id}>{rec.deliverables?.[d.id] ? "☑" : "☐"} {d.label}</li>)}</ul>
            <SectionThread section="deliverables" comments={comments} post={post} markRead={markRead} me="host" /></div>
          <SectionThread section="general" comments={comments} post={post} markRead={markRead} me="host" label="Conversation about this day" />
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-[#0B2A4A]">Feedback to student</label>
            <Textarea rows={2} value={fb} onChange={(e) => setFb(e.target.value)} data-testid={`host-feedback-input-${day.day}`} />
            <div className="flex gap-2">
              <button onClick={save} className="rounded-lg bg-[#1A9AA8] text-white px-4 py-2 text-sm font-semibold" data-testid={`host-feedback-save-${day.day}`}>Save feedback</button>
              <Link to={`/print/day/${day.day}?mode=filled&student=${userId}`} className="inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm font-semibold"><Printer className="h-4 w-4" /> Print</Link>
            </div>
          </div>
        </div>
      )}
    </details>
  );
}

export default function HostPerson() {
  const { email } = useParams();
  useSeo({ title: "Student profile", path: "/host", noindex: true });
  const [p, setP] = useState(null);
  const [course, setCourse] = useState(null);
  const [viaEmail, setViaEmail] = useState(false);
  const load = () => api.get(`/admin/people/${encodeURIComponent(email)}`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); fetchCourse().then(setCourse); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <Spinner />;

  const app = p.application;
  const lead = p.leads[0];
  const name = p.user?.name || app?.name || lead?.name || p.email;
  const wa = (app?.whatsapp || lead?.whatsapp || "").replace(/[^\d]/g, "");
  const waConsent = app?.whatsapp_consent || lead?.whatsapp_consent;
  const greet = encodeURIComponent(`Hi ${name.split(" ")[0]}, this is Greg from Digital Nomad Nicaragua.`);
  const send = async (body) => {
    try {
      await api.post(`/admin/people/${encodeURIComponent(p.email)}/messages`, { body, via_email: viaEmail });
      toast.success(viaEmail ? "Sent in-app and by email" : "Sent in-app");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };
  const decide = async (action) => { await decideApplication(app.id, action); load(); };
  const byDay = Object.fromEntries((p.progress || []).map((x) => [x.day, x]));
  return <PersonView {...{ p, course, viaEmail, setViaEmail, load, app, lead, name, wa, waConsent, greet, send, decide, byDay }} />;
}

function PersonView({ p, course, viaEmail, setViaEmail, load, app, lead, name, wa, waConsent, greet, send, decide, byDay }) {
  const insights = useInsights(p.user?.id);

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="host-person-page">
      <Link to="/host" className="text-sm font-semibold text-[#147C88] inline-flex items-center gap-1" data-testid="person-back-link"><ArrowLeft className="h-4 w-4" /> Host dashboard</Link>
      <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A] flex items-center gap-3" data-testid="person-name">{p.user && <Avatar user={p.user} className="h-12 w-12 text-base" testId="person-avatar" />}{name}</h1>
          <p className="mt-1 text-slate-600">{p.email}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {lead && <StatusBadge status={lead.status} testId="person-lead-status" />}
            {app ? <StatusBadge status={app.status} testId="person-application-status" /> : <span className="text-xs text-slate-500">{p.user ? "Has account · no application" : "No account yet"}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {p.user && <StartCallButton studentId={p.user.id} />}
          {wa && waConsent ? (
            <a href={`https://wa.me/${wa}?text=${greet}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] text-white px-4 py-2.5 text-sm font-semibold" data-testid="admin-whatsapp-btn"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-xl border border-dashed px-4 py-2.5 text-xs text-slate-500" data-testid="admin-whatsapp-unavailable">{wa ? "No WhatsApp consent" : "No WhatsApp number"}</span>
          )}
          <a href={`mailto:${p.email}?subject=${encodeURIComponent("Digital Nomad Nicaragua")}`} className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2.5 text-sm font-semibold" data-testid="admin-email-btn"><Mail className="h-4 w-4" /> Open in my email</a>
          {app && app.status !== "approved" && <button onClick={() => decide("approve")} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold" data-testid="person-approve-btn"><Check className="h-4 w-4" /> Approve</button>}
          {app && app.status !== "declined" && <button onClick={() => decide("decline")} className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 text-rose-700 px-4 py-2.5 text-sm font-semibold" data-testid="person-decline-btn"><X className="h-4 w-4" /> Decline</button>}
        </div>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "profile"} className="mt-8">
        <TabsList className="bg-[#0B2A4A]/5">
          <TabsTrigger value="profile" data-testid="person-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="work" data-testid="person-tab-work">Course work</TabsTrigger>
          <TabsTrigger value="messages" data-testid="person-tab-messages">Messages ({p.messages.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-6 flex flex-col gap-5">
          {app && (
            <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6" data-testid="person-application-card">
              <h3 className="font-semibold text-[#0B2A4A] mb-3">Course application · {fmtDate(app.created_at)}</h3>
              <dl>
                <KV k="Stage" v={APP_STAGES.find((s) => s.value === app.stage)?.label} /><KV k="Format" v={COURSE_FORMATS.find((s) => s.value === app.format)?.label} /><KV k="Goals" v={app.goals} /><KV k="Skills & interests" v={app.skills} />
                <KV k="Existing work / offer" v={app.existing_work} /><KV k="Collaboration" v={app.collaboration} /><KV k="WhatsApp" v={app.whatsapp} />
                <KV k="WhatsApp consent" v={app.whatsapp_consent} /><KV k="Future retreats" v={app.retreat_optin} /><KV k="Add-ons" v={app.add_ons} /><KV k="Other add-on" v={app.add_ons_other} /><KV k="Reviewed" v={fmtDate(app.reviewed_at)} />
              </dl>
            </div>
          )}
          {app?.status === "approved" && <HostQuestionnaire userId={p.user.id} />}
          {p.user && <HostAvenueAccess userId={p.user.id} />}
          {p.leads.map((l) => <LeadCard key={l.id} lead={l} onUpdate={load} />)}
        </TabsContent>
        <TabsContent value="work" className="mt-6 flex flex-col gap-3">
          {p.user && <button onClick={load} className="self-end inline-flex items-center gap-1 rounded-lg border border-[#0B2A4A]/20 px-3 py-1.5 text-xs font-semibold hover:border-[#1A9AA8]" data-testid="person-refresh-answers-btn">Refresh answers</button>}
          {!p.user || app?.status !== "approved" ? <p className="text-sm text-slate-500">Course work appears after the application is approved.</p> : !course ? <Spinner /> : (
            <>
              <Link to={`/print/worksheets?mode=filled&student=${p.user.id}`} className="self-start inline-flex items-center gap-2 rounded-xl border bg-white px-4 py-2 text-sm font-semibold" data-testid="person-print-all-btn"><Printer className="h-4 w-4" /> Print all of this student's worksheets</Link>
              <HostCertificates studentId={p.user.id} />
              <AiInsights studentId={p.user.id} scope="course" list={insights.list} reload={insights.load} title="Whole course summary (AI)" />
              {course.days.map((d) => <DayWork key={d.day} day={d} ws={course.worksheets.find((w) => w.id === d.worksheet_id)} rec={byDay[d.day]} userId={p.user.id} insights={insights} />)}
            </>
          )}
        </TabsContent>
        <TabsContent value="messages" className="mt-6 max-w-2xl">
          <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6">
            {!p.user && <p className="mb-4 text-xs text-amber-700">This lead has no account yet, so in-app messages will be visible once they sign up. Tick "also send by email" to reach them now.</p>}
            <MessageThread messages={p.messages} onSend={send} me="host" placeholder={`Reply to ${name}...`}
              extra={<CheckRow checked={viaEmail} onChange={setViaEmail} testId="message-via-email-checkbox">Also send by email</CheckRow>} />
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
